import api from './api';

/** Buyers Logic Ag staff can work on. */
export interface BuyerSummary {
  slug: string;
  name: string;
}

/** A mill or a supplier: a name, the buyer's own code, and whether it can still be picked. */
export interface NamedRow {
  id: string;
  name: string;
  code: string | null;
  active: boolean;
}

export type NamedResource = 'mills' | 'suppliers';

export interface Unit {
  id: string;
  code: string;
  name: string;
  pounds_per_unit: string | null;
  active: boolean;
}

export interface Ingredient {
  id: string;
  name: string;
  item_code: string | null;
  item_name: string | null;
  unit_id: string;
  unit_code: string;
  pounds_per_bushel: string | null;
  active: boolean;
}

export interface NamedRowInput {
  name: string;
  code: string | null;
}

export interface IngredientInput {
  name: string;
  item_code: string | null;
  item_name: string | null;
  unit_id: string;
  pounds_per_bushel: string | null;
}

/** A futures contract a buyer hedges on, e.g. CBOT corn (ZC). */
export interface FuturesContract {
  id: string;
  code: string;
  name: string;
  exchange: string;
  contract_size: string;
  unit_id: string;
  unit_code: string;
  pounds_per_bushel: string | null;
  /** The month letters it trades, in calendar order, e.g. "HKNUZ". */
  listed_months: string;
  active: boolean;
}

/** One coming contract month, e.g. "Dec 2026 (ZCZ26)". */
export interface FuturesMonth {
  year: number;
  month: number;
  symbol: string;
  label: string;
}

/** The contract an ingredient hedges on, and how much of it; a null ratio is not set yet. */
export interface HedgeMapping {
  id: string;
  ingredient_id: string;
  ingredient_name: string;
  contract_id: string;
  contract_code: string;
  ratio: string | null;
  active: boolean;
}

export interface FuturesContractInput {
  name: string;
  exchange: string;
  contract_size: string;
  unit_id: string;
  pounds_per_bushel: string | null;
  listed_months: string;
}

export interface HedgeMappingInput {
  contract_id: string;
  ratio: string | null;
}

export const buyersApi = {
  list: () => api.get<BuyerSummary[]>('/buyers'),
};

export const referenceApi = {
  listNamed: (slug: string, resource: NamedResource) => api.get<NamedRow[]>(`/buyers/${slug}/${resource}`),
  addNamed: (slug: string, resource: NamedResource, body: NamedRowInput) =>
    api.post<NamedRow>(`/buyers/${slug}/${resource}`, body),
  editNamed: (slug: string, resource: NamedResource, id: string, body: Partial<NamedRowInput & { active: boolean }>) =>
    api.patch<NamedRow>(`/buyers/${slug}/${resource}/${id}`, body),

  listUnits: (slug: string) => api.get<Unit[]>(`/buyers/${slug}/units`),

  listIngredients: (slug: string) => api.get<Ingredient[]>(`/buyers/${slug}/ingredients`),
  addIngredient: (slug: string, body: IngredientInput) => api.post<Ingredient>(`/buyers/${slug}/ingredients`, body),
  editIngredient: (slug: string, id: string, body: Partial<IngredientInput & { active: boolean }>) =>
    api.patch<Ingredient>(`/buyers/${slug}/ingredients/${id}`, body),

  listContracts: (slug: string) => api.get<FuturesContract[]>(`/buyers/${slug}/futures-contracts`),
  addContract: (slug: string, body: FuturesContractInput & { code: string }) =>
    api.post<FuturesContract>(`/buyers/${slug}/futures-contracts`, body),
  editContract: (slug: string, id: string, body: Partial<FuturesContractInput & { active: boolean }>) =>
    api.patch<FuturesContract>(`/buyers/${slug}/futures-contracts/${id}`, body),
  listMonths: (slug: string, id: string, count = 3) =>
    api.get<FuturesMonth[]>(`/buyers/${slug}/futures-contracts/${id}/months`, { params: { count } }),

  listMappings: (slug: string) => api.get<HedgeMapping[]>(`/buyers/${slug}/hedge-mappings`),
  addMapping: (slug: string, body: HedgeMappingInput & { ingredient_id: string }) =>
    api.post<HedgeMapping>(`/buyers/${slug}/hedge-mappings`, body),
  editMapping: (slug: string, id: string, body: Partial<HedgeMappingInput & { active: boolean }>) =>
    api.patch<HedgeMapping>(`/buyers/${slug}/hedge-mappings/${id}`, body),
};
